import React, { useState, useEffect, useCallback } from 'react';
import Layout from '../../components/Layout';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Layers, Plus, ChevronLeft, X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { mfgAPI } from '../../services/api';
import { toast } from 'sonner';
import { RecordActions } from '../../components/common/RecordActions';
import { RecordViewDialog } from '../../components/common/RecordViewDialog';
import { useAuth } from '../../context/AuthContext';

const USAGES = ['PRODUCTION', 'ENGINEERING', 'COSTING', 'UNIVERSAL'];

const BOMs = () => {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [rows, setRows] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [uoms, setUoms] = useState([]);
  const [plants, setPlants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [viewItem, setViewItem] = useState(null);
  const [header, setHeader] = useState({ material_id: '', plant_id: '', bom_usage: 'PRODUCTION', base_quantity: 1, base_uom: '', status: 'ACTIVE' });
  const [components, setComponents] = useState([]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const [bomRes, matRes, uomRes, plantRes] = await Promise.all([
        mfgAPI.getBOMs(), mfgAPI.getMaterials(), mfgAPI.getUOMs(), mfgAPI.getPlants(),
      ]);
      setRows(bomRes.data);
      setMaterials(matRes.data);
      setUoms(uomRes.data);
      setPlants(plantRes.data);
    } catch (e) {
      toast.error('Failed to load BOMs');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openCreate = () => {
    setEditing(null);
    setHeader({ material_id: '', plant_id: '', bom_usage: 'PRODUCTION', base_quantity: 1, base_uom: '', status: 'ACTIVE' });
    setComponents([{ item_no: 10, component_material_id: '', quantity: 1, uom: '', scrap_pct: 0 }]);
    setOpen(true);
  };
  const openEdit = (bom) => {
    setEditing(bom);
    setHeader({ material_id: bom.material_id, plant_id: bom.plant_id, bom_usage: bom.bom_usage, base_quantity: bom.base_quantity, base_uom: bom.base_uom, status: bom.status });
    setComponents(bom.components.map((c) => ({ ...c })));
    setOpen(true);
  };

  const addComp = () => {
    const nextNo = components.length ? Math.max(...components.map((c) => c.item_no)) + 10 : 10;
    setComponents([...components, { item_no: nextNo, component_material_id: '', quantity: 1, uom: '', scrap_pct: 0 }]);
  };
  const setComp = (idx, k, v) => setComponents(components.map((c, i) => (i === idx ? { ...c, [k]: v } : c)));
  const removeComp = (idx) => setComponents(components.filter((_, i) => i !== idx));

  const submit = async () => {
    if (!header.material_id || !header.plant_id || !header.base_uom) {
      toast.error('Material, Plant and Base UoM are required'); return;
    }
    const validComps = components.filter((c) => c.component_material_id && Number(c.quantity) > 0);
    if (validComps.length === 0) { toast.error('Add at least one component'); return; }
    const payload = {
      ...header,
      base_quantity: Number(header.base_quantity),
      components: validComps.map((c) => ({
        item_no: Number(c.item_no),
        component_material_id: c.component_material_id,
        quantity: Number(c.quantity),
        uom: c.uom || header.base_uom,
        scrap_pct: Number(c.scrap_pct || 0),
        item_category: 'L',
      })),
    };
    try {
      if (editing) { await mfgAPI.updateBOM(editing.bom_id, payload); toast.success('BOM updated'); }
      else { await mfgAPI.createBOM(payload); toast.success('BOM created'); }
      setOpen(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };

  const remove = async (bom) => {
    if (!window.confirm(`Delete BOM ${bom.bom_id}?`)) return;
    try { await mfgAPI.deleteBOM(bom.bom_id); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Delete failed'); }
  };
  void remove;

  return (
    <Layout>
      <div className="p-6 space-y-6" data-testid="boms-page">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate('/manufacturing')}><ChevronLeft className="h-5 w-5" /></Button>
            <div className="p-2 bg-orange-600 rounded-lg"><Layers className="h-5 w-5 text-white" /></div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Bills of Material</h1>
              <p className="text-sm text-slate-500">Product structures &amp; component lists</p>
            </div>
          </div>
          {canWrite('bom') && (<Button onClick={openCreate} className="bg-orange-600 hover:bg-orange-700" data-testid="create-bom-btn"><Plus className="h-4 w-4 mr-2" /> Add BOM</Button>)}
        </div>

        <Card className="border-0 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50 hover:bg-slate-50">
                  <TableHead className="font-semibold">BOM</TableHead>
                  <TableHead className="font-semibold">Material</TableHead>
                  <TableHead className="font-semibold">Plant</TableHead>
                  <TableHead className="font-semibold">Usage</TableHead>
                  <TableHead className="font-semibold text-center">Components</TableHead>
                  <TableHead className="font-semibold">Status</TableHead>
                  <TableHead className="font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loading ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">Loading…</TableCell></TableRow>
                ) : rows.length === 0 ? (
                  <TableRow><TableCell colSpan={7} className="text-center py-10 text-slate-400">No BOMs yet</TableCell></TableRow>
                ) : rows.map((b) => (
                  <TableRow key={b.bom_id} data-testid={`bom-row-${b.bom_id}`} className="hover:bg-orange-50/40">
                    <TableCell className="font-mono font-medium text-orange-600">{b.bom_id}</TableCell>
                    <TableCell>{b.material_id}</TableCell>
                    <TableCell>{b.plant_id}</TableCell>
                    <TableCell><Badge variant="outline">{b.bom_usage}</Badge></TableCell>
                    <TableCell className="text-center">{b.components?.length || 0}</TableCell>
                    <TableCell><Badge className={b.status === 'ACTIVE' ? 'bg-emerald-100 text-emerald-700 border-0' : 'bg-slate-200 text-slate-600 border-0'}>{b.status}</Badge></TableCell>
                    <TableCell className="text-right">
                      <RecordActions
                        doctype="bom"
                        idForTestId={b.bom_id}
                        onView={() => setViewItem(b)}
                        onEdit={() => openEdit(b)}
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Card>

        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>{editing ? `Edit BOM ${editing.bom_id}` : 'Create BOM'}</DialogTitle>
              <DialogDescription>Define the finished product and its component list (zero free-text — pick from master data).</DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-3 gap-4 py-2">
              <div>
                <Label>Material *</Label>
                <Select value={header.material_id} onValueChange={(v) => setHeader({ ...header, material_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="bom-material-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{materials.map((m) => <SelectItem key={m.material_number} value={m.material_number}>{m.material_number} — {m.description}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Plant *</Label>
                <Select value={header.plant_id} onValueChange={(v) => setHeader({ ...header, plant_id: v })} disabled={!!editing}>
                  <SelectTrigger data-testid="bom-plant-select"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>{plants.map((p) => <SelectItem key={p.plant_id} value={p.plant_id}>{p.plant_id}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Usage</Label>
                <Select value={header.bom_usage} onValueChange={(v) => setHeader({ ...header, bom_usage: v })}>
                  <SelectTrigger data-testid="bom-usage-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{USAGES.map((u) => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Base Quantity</Label>
                <Input type="number" data-testid="bom-baseqty-input" value={header.base_quantity} onChange={(e) => setHeader({ ...header, base_quantity: e.target.value })} />
              </div>
              <div>
                <Label>Base UoM *</Label>
                <Select value={header.base_uom} onValueChange={(v) => setHeader({ ...header, base_uom: v })}>
                  <SelectTrigger data-testid="bom-baseuom-select"><SelectValue placeholder="UoM" /></SelectTrigger>
                  <SelectContent>{uoms.map((u) => <SelectItem key={u.unit_code} value={u.unit_code}>{u.unit_code}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Status</Label>
                <Select value={header.status} onValueChange={(v) => setHeader({ ...header, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="ACTIVE">ACTIVE</SelectItem><SelectItem value="INACTIVE">INACTIVE</SelectItem></SelectContent>
                </Select>
              </div>
            </div>

            <div className="border-t pt-3">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold text-slate-800">Components</h3>
                <Button size="sm" variant="outline" onClick={addComp} data-testid="add-component-btn"><Plus className="h-3.5 w-3.5 mr-1" /> Add Component</Button>
              </div>
              <div className="space-y-2">
                {components.map((c, idx) => (
                  <div key={idx} className="grid grid-cols-12 gap-2 items-end" data-testid={`component-row-${idx}`}>
                    <div className="col-span-1">
                      <Label className="text-xs">Item</Label>
                      <Input type="number" value={c.item_no} onChange={(e) => setComp(idx, 'item_no', e.target.value)} />
                    </div>
                    <div className="col-span-5">
                      <Label className="text-xs">Component Material</Label>
                      <Select value={c.component_material_id} onValueChange={(v) => setComp(idx, 'component_material_id', v)}>
                        <SelectTrigger data-testid={`comp-material-select-${idx}`}><SelectValue placeholder="Select" /></SelectTrigger>
                        <SelectContent>{materials.map((m) => <SelectItem key={m.material_number} value={m.material_number}>{m.material_number} — {m.description}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">Quantity</Label>
                      <Input type="number" value={c.quantity} onChange={(e) => setComp(idx, 'quantity', e.target.value)} />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-xs">UoM</Label>
                      <Select value={c.uom} onValueChange={(v) => setComp(idx, 'uom', v)}>
                        <SelectTrigger><SelectValue placeholder="UoM" /></SelectTrigger>
                        <SelectContent>{uoms.map((u) => <SelectItem key={u.unit_code} value={u.unit_code}>{u.unit_code}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="col-span-1">
                      <Label className="text-xs">Scrap%</Label>
                      <Input type="number" value={c.scrap_pct} onChange={(e) => setComp(idx, 'scrap_pct', e.target.value)} />
                    </div>
                    <div className="col-span-1">
                      <Button size="icon" variant="ghost" className="text-red-500" onClick={() => removeComp(idx)}><X className="h-4 w-4" /></Button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={submit} className="bg-orange-600 hover:bg-orange-700" data-testid="save-bom-btn">{editing ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <RecordViewDialog
          open={!!viewItem}
          onOpenChange={(o) => { if (!o) setViewItem(null); }}
          title={`BOM ${viewItem?.bom_id || ''}`}
          record={viewItem}
        />
      </div>
    </Layout>
  );
};

export default BOMs;
